import type { Module, Screen } from "./catalog";
import type { Row } from "./api";

// Presentation separation only. Existing service authorization remains authoritative.
export const canUseTechnicalDetails = (role: unknown): boolean =>
  role === "workspace_admin";
const preparationPaths = new Set([
  "/workspace/dashboard",
  "/workspace/cases",
  "/workspace/assignments",
  "/workspace/activity",
  "/workspace/timeline",
  "/workspace/notifications",
  "/intake/new",
  "/intake/questionnaire",
  "/intake/facts",
  "/intake/supplemental",
  "/intake/evidence-upload",
  "/intake/w2-upload",
  "/intake/evidence-review",
  "/intake/reconciliation",
  "/intake/admission",
  "/evidence/viewer",
  "/evidence/timeline",
  "/evidence/comparison",
  "/evidence/duplicates",
  "/evidence/conflicts",
  "/review/preparer",
  "/review/reviewer",
  "/review/independent",
  "/review/findings",
  "/forms/1040",
  "/productivity/tasks",
  "/productivity/saved-views",
  "/productivity/pinned",
  "/settings/preferences",
  "/auth/login",
  "/auth/password-reset",
  "/auth/mfa",
  "/auth/sessions",
  "/auth/devices",
]);
export const isTechnicalScreen = (screen: Pick<Screen, "path">): boolean =>
  !preparationPaths.has(screen.path);
export function preparationNavigation(
  all: Module[],
  role: unknown,
  requestedTechnical: boolean,
): Module[] {
  const technical = canUseTechnicalDetails(role) && requestedTechnical;
  return all
    .map((module) => ({
      ...module,
      screens: module.screens.filter(
        (screen) => technical || !isTechnicalScreen(screen),
      ),
    }))
    .filter((module) => module.screens.length > 0);
}

export function draftNeedsAttention(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return true;
  const unresolved = (item: unknown): boolean => {
    if (typeof item === "string")
      return ["UNKNOWN", "CONFLICT", "UNRESOLVED", "PENDING_POSTCALC"].includes(
        item,
      );
    if (Array.isArray(item)) return item.some(unresolved);
    if (item && typeof item === "object")
      return (
        ((item as Row).state === "KNOWN" && !Object.hasOwn(item, "value")) ||
        Object.values(item).some(unresolved)
      );
    return false;
  };
  return (
    (typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0) ||
    unresolved(value)
  );
}
export function intakeProgress(questions: Row[], answers: Row = {}) {
  const asked = questions.filter((q) => q.stage === "PRECALC");
  const saved = asked.filter((q) => Object.hasOwn(answers, q.id));
  return {
    total: asked.length,
    saved: saved.length,
    attention: asked.filter((q) => draftNeedsAttention(answers[q.id])).length,
    later: questions.length - asked.length,
  };
}

export function preparationError(
  error: unknown,
  context?: { sectionLabel?: string; fieldLabel?: string },
): string {
  const code = (error as Row)?.code;
  if (code === "REVISION_CONFLICT")
    return "This return changed while you were working. Refresh it, review the latest answers, and try again.";
  if (code === "FORBIDDEN" || code === "DERIVED_CAPTURE_FORBIDDEN")
    return "Your role cannot make this change. Ask your preparer or workspace administrator for help.";
  if (code === "DRAFT_VALUE_SHAPE_INVALID")
    return `${[context?.sectionLabel, context?.fieldLabel].filter(Boolean).join(" — ") || "The submitted entry"}: the saved entry was rejected because its structure differs from the supported form. No changes were confirmed. Reopen ${context?.sectionLabel ? "this section" : "the affected section"} and use its field-level guidance before saving again.`;
  if (code === "AUTHENTICATION_REQUIRED")
    return "Your session has ended. Sign in again to continue.";
  if (code === "EVIDENCE_DUPLICATE")
    return "This document has already been added to the return.";
  return "We could not confirm whether the request completed. Refresh and check the latest saved information before trying again.";
}

/** Operational failures are not field-validation findings; never expose service payloads. */
export function workspaceOperationError(
  error: unknown,
  operation: string,
): string {
  const code = (error as Row)?.code;
  if (code === "AUTHENTICATION_REQUIRED" || code === "CSRF_INVALID")
    return `Your session needs to be refreshed before you can ${operation}. Sign in again, then check the latest saved information.`;
  if (code === "FORBIDDEN")
    return `Your current workspace role does not allow you to ${operation}. Ask a workspace administrator for help.`;
  if (code === "NOT_FOUND")
    return `The record needed to ${operation} is no longer available. Refresh the workspace before trying again.`;
  if (code === "RATE_LIMITED")
    return `Too many requests were made to ${operation}. Wait briefly, then refresh before trying again.`;
  return `We could not confirm the request to ${operation}. Refresh and check the latest saved information before trying again.`;
}

export const notesForReturn = (records: Row[], returnId: unknown): Row[] =>
  typeof returnId === "string" && returnId
    ? records.filter((record) => record.caseId === returnId)
    : [];
export const isDocumentActivity = (event: Row): boolean =>
  event.type === "evidence" || event.action === "evidence.uploaded";
